import { MemoryRepositoryConfig } from '../config/repository.config.js';
import type {
  IStatsProvider,
  ISyncRepository,
} from '../interfaces/repository.interface.js';
import type { TranslationData } from '../types/translation-data.types.js';
import { flattenTranslations } from '../utils/index.js';
import { MemoryRepository } from './memory-repository.js';

/**
 * A bundle of translations to seed offline-first: locale → namespace → data,
 * where `data` is the raw `{ key: ContentUnit }` map the backend returns (and
 * the CLI `snapshot` command writes). Styles MUST be preserved (no flattening
 * to display strings), so inline markup renders offline.
 */
export type StaticBundle = Record<string, Record<string, TranslationData>>;

/**
 * Read-only, pre-seeded sync repository for OFFLINE RESILIENCE. Bundle a
 * snapshot (or import the CLI's snapshot files) and feed it in; `t()` then
 * resolves synchronously from the bundle even with the server down and nothing
 * cached — the gap browser localStorage/Cache can't fill on a first load.
 *
 * It seeds an internal MemoryRepository exactly the way `SyncHandler.saveNamespace`
 * does (the namespace blob `@locale:namespace` + flattened `locale:namespace:key`
 * entries, each a JSON ContentUnit), so the standard SyncHandler resolves it with
 * zero special-casing. Writes are no-ops: when the network succeeds, the LIVE
 * MemoryRepository (priority 0) holds the fresh value and shadows this layer
 * (which sits at priority 0.5), so the bundle is never overwritten or corrupted.
 * TTL/size are effectively infinite so the bundle never expires or evicts.
 */
export class StaticRepository
  implements ISyncRepository<MemoryRepositoryConfig>, IStatsProvider
{
  readonly name = 'StaticRepository';
  readonly type = 'memory' as const;
  readonly config: MemoryRepositoryConfig;
  private readonly mem: MemoryRepository;

  constructor(bundle: StaticBundle, config: MemoryRepositoryConfig = {}) {
    this.config = {
      enabled: config.enabled ?? true,
      priority: config.priority ?? 0.5, // after the live memory (0), before storage
      options: {
        ttl: Number.MAX_SAFE_INTEGER,
        maxSize: Number.MAX_SAFE_INTEGER,
        ...config.options,
      },
    };
    this.mem = new MemoryRepository({ options: { maxSize: Number.MAX_SAFE_INTEGER } });

    for (const [locale, namespaces] of Object.entries(bundle)) {
      for (const [namespace, translations] of Object.entries(namespaces)) {
        this.mem.setNamespace(locale, namespace, translations);
        const flat = flattenTranslations(
          translations as Record<string, unknown>,
          '',
          locale,
          namespace
        );
        for (const [key, value] of Object.entries(flat)) this.mem.set(key, value);
      }
    }
  }

  get(key: string): string | null {
    return this.mem.get(key);
  }

  getNamespace(locale: string, namespace: string): TranslationData | null {
    return this.mem.getNamespace(locale, namespace);
  }

  getEntries(): Record<string, string> {
    return this.mem.getEntries();
  }

  getConfig(): MemoryRepositoryConfig {
    return this.config;
  }

  // Read-only: the live memory layer owns fresh network data; this stays pristine.
  set(): void {}
  remove(): void {}
  clear(): void {}
  setNamespace(): void {}
}
